// ══════════════════════════════════════════════════════════════════
// BadgeCardPreview.jsx — on-screen flip preview of the printable
// employee ID badge (front + back), used by the "Badge View" modal in
// TeamView.jsx. Portrait bilingual design, approved with the client
// across several rounds of wireframe previews — same design language /
// colour tokens as the PDF built in utils/badgeGenerator.js — keep
// both in sync if either changes.
//
// Layout is a direct scale-up (×1.3) of the approved 230px-wide HTML
// wireframe (badge_wireframe_preview.html), so proportions match the
// printed badge exactly.
//
// The watermark is an absolutely-positioned image placed BEFORE the
// visible content in the DOM. Per CSS2.1 painting order, a positioned
// descendant with z-index:auto paints after in-flow siblings regardless
// of DOM order — so without an explicit stacking fix, the watermark
// would paint OVER the header/photo/name. Fix: give the watermark
// z-index:0 and wrap all visible content in one sibling wrapper with
// position:relative; z-index:1.
// ══════════════════════════════════════════════════════════════════
import React from 'react';
import { motion } from 'framer-motion';
import { RotateCw, Phone, Mail, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { BADGE_COLORS } from '../utils/badgeGenerator';
import type { TeamMember } from '../hooks/useTeamController';

const COMPANY_NAME_L1 = 'AL-RAMMAH INTERNATIONAL';
const COMPANY_NAME_L2 = 'Telecom & Information System Services';
const CARD_DISCLAIMER = 'This card remains the property of the company and must be returned upon termination of employment.';
const CONTACT_ROWS = [
    { Icon: Phone, text: '+966 13 896 1700' },
    { Icon: Mail, text: 'info@goritis.com' },
    { Icon: Globe, text: 'www.goritis.com' },
];
const ARABIC_FONT_STACK = "'Segoe UI', Tahoma, Arial, sans-serif";

// Badge validity, used only when a member has no explicit badge_expiry_date
// (mirrors DEFAULT_VALIDITY_YEARS in utils/badgeGenerator.js).
const DEFAULT_VALIDITY_YEARS = 3;

const initialsOf = (name?: string | null) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

// Parses a 'YYYY-MM-DD' DB date string as local midnight so formatting
// never shifts a day off in either direction.
const parseDbDate = (s?: string | null): Date | null => {
    if (!s) return null;
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

interface BadgeCardPreviewProps {
    member: TeamMember;
    flipped: boolean;
    onFlip: () => void;
}

const BadgeCardPreview = ({ member, flipped, onFlip }: BadgeCardPreviewProps) => {
    const expiryDate = parseDbDate(member.badge_expiry_date)
        || new Date((parseDbDate(member.badge_issue_date) || new Date()).getFullYear() + DEFAULT_VALIDITY_YEARS,
            (parseDbDate(member.badge_issue_date) || new Date()).getMonth(),
            (parseDbDate(member.badge_issue_date) || new Date()).getDate());
    const expiryLabel = fmtDate(expiryDate);

    const gridFields = [
        { label: 'NATIONALITY', value: member.nationality },
        { label: 'DEPARTMENT', value: member.department },
        { label: 'BLOOD TYPE', value: member.blood_type },
    ];

    return (
        <div style={{ width: '100%', maxWidth: 300, margin: '0 auto' }}>
            <div style={{ perspective: 1600 }}>
                <motion.div
                    onClick={onFlip}
                    animate={{ rotateY: flipped ? 180 : 0 }}
                    transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                        position: 'relative', width: '100%', aspectRatio: '54 / 85.6',
                        transformStyle: 'preserve-3d', cursor: 'pointer',
                    }}>

                    {/* ── FRONT ── */}
                    <div style={{
                        position: 'absolute', inset: 0, backfaceVisibility: 'hidden', overflow: 'hidden',
                        background: '#ffffff', border: `2px solid ${BADGE_COLORS.panelBorder}`,
                        borderRadius: 18, boxShadow: '0 16px 40px rgba(15,23,42,0.18)',
                        boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
                        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
                    }}>
                        <img src="/avatars/RITIS-watermark-dark.png" alt="" style={{
                            position: 'absolute', zIndex: 0, top: '64%', left: '50%', transform: 'translate(-50%, -50%)',
                            width: '58%', pointerEvents: 'none',
                        }} />

                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                            {/* header */}
                            <div style={{ padding: '16px 18px 10px', display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                                <img src="/avatars/RITIS.png" alt="" style={{ width: 26, height: 26, flexShrink: 0, marginTop: 3 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: BADGE_COLORS.ink, lineHeight: 1.25 }}>{COMPANY_NAME_L1}</div>
                                    <div style={{ fontSize: 7, fontWeight: 500, color: BADGE_COLORS.sub, lineHeight: 1.3, marginTop: 1 }}>{COMPANY_NAME_L2}</div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: BADGE_COLORS.tealDark, marginTop: -3, marginBottom: 9 }}>EMPLOYEE ID CARD</div>
                            <div style={{ height: 2, background: BADGE_COLORS.teal, margin: '0 18px 16px' }} />

                            {/* photo */}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <div style={{ padding: 4, border: `3px solid ${BADGE_COLORS.teal}`, background: '#fff' }}>
                                    {member.avatar ? (
                                        <img src={member.avatar} alt={member.name} style={{ width: 112, height: 112, objectFit: 'cover', display: 'block' }} />
                                    ) : (
                                        <div style={{ width: 112, height: 112, background: BADGE_COLORS.tealDark, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 30 }}>
                                            {initialsOf(member.name)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* names */}
                            <div style={{ textAlign: 'center', marginTop: 16, padding: '0 13px' }}>
                                <div style={{ fontSize: 17, fontWeight: 800, color: BADGE_COLORS.ink, lineHeight: 1.2 }}>{member.name || '—'}</div>
                                {member.name_arabic && (
                                    <div dir="rtl" style={{ fontFamily: ARABIC_FONT_STACK, fontWeight: 700, fontSize: 18, color: BADGE_COLORS.ink, marginTop: 4 }}>
                                        {member.name_arabic}
                                    </div>
                                )}
                            </div>

                            <div style={{ textAlign: 'center', marginTop: 10 }}>
                                <div style={{ color: BADGE_COLORS.tealDark, fontSize: 13, fontWeight: 800 }}>{(member.position || '').toUpperCase()}</div>
                            </div>

                            {/* Nationality / Department / Blood Type */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18, padding: '0 21px' }}>
                                {gridFields.map((f, i) => (
                                    <React.Fragment key={f.label}>
                                        {i > 0 && <div style={{ width: 1, background: '#e2e8f0', margin: '1px 10px' }} />}
                                        <div style={{ textAlign: 'center', flex: 1 }}>
                                            <div style={{ color: BADGE_COLORS.sub, fontSize: 7, fontWeight: 800, letterSpacing: '0.08em' }}>{f.label}</div>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: BADGE_COLORS.ink, marginTop: 4 }}>{f.value || '—'}</div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>

                            <div style={{ flex: 1 }} />

                            <div style={{ textAlign: 'center', marginBottom: 13 }}>
                                <div style={{ color: BADGE_COLORS.sub, fontSize: 9, fontWeight: 800, letterSpacing: '0.08em' }}>EMPLOYEE ID</div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: BADGE_COLORS.ink, marginTop: 3 }}>{member.employee_id || 'Not assigned'}</div>
                            </div>

                            <div style={{ height: 1, background: '#e2e8f0', margin: '0 18px' }} />
                            <div style={{ textAlign: 'center', padding: '10px 0 16px' }}>
                                <div style={{ color: BADGE_COLORS.sub, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em' }}>VALID UNTIL</div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: BADGE_COLORS.ink, marginTop: 3 }}>{expiryLabel}</div>
                            </div>
                        </div>
                    </div>

                    {/* ── BACK ── */}
                    <div style={{
                        position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', overflow: 'hidden',
                        background: BADGE_COLORS.panelBg, border: `2px solid ${BADGE_COLORS.panelBorder}`,
                        borderRadius: 18, boxShadow: '0 16px 40px rgba(15,23,42,0.18)',
                        boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
                        color: '#fff', fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
                    }}>
                        <img src="/avatars/RITIS-watermark-light.png" alt="" style={{
                            position: 'absolute', zIndex: 0, top: '56%', left: '50%', transform: 'translate(-50%, -50%)',
                            width: '72%', pointerEvents: 'none',
                        }} />

                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, alignItems: 'center', textAlign: 'center' }}>
                            <div style={{ fontSize: 17, fontWeight: 800, marginTop: 21, padding: '0 16px' }}>{COMPANY_NAME_L1}</div>
                            <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.7, marginTop: 5, padding: '0 26px', lineHeight: 1.3 }}>{COMPANY_NAME_L2}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginTop: 13, opacity: 0.9 }}>EMPLOYEE IDENTIFICATION CARD</div>

                            <div style={{ marginTop: 18, background: '#fff', padding: 10, borderRadius: 8 }}>
                                <QRCodeSVG value={`RITIS-EMPLOYEE:${member.employee_id || (member.id != null ? `RITIS-${member.id}` : 'RITIS-EMPLOYEE')}`}
                                    size={96} level="M" fgColor={BADGE_COLORS.panelBg} bgColor="#ffffff" />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 12 }}>Scan to Verify Employee</div>

                            <div style={{ width: '80%', height: 1, background: 'rgba(255,255,255,0.35)', margin: '16px 0' }} />

                            <div style={{ fontSize: 8, lineHeight: 1.5, opacity: 0.9, padding: '0 18px' }}>
                                {CARD_DISCLAIMER}
                            </div>

                            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', marginTop: 16, opacity: 0.9 }}>IF FOUND, PLEASE RETURN TO</div>
                            <div style={{ fontSize: 10, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
                                {CONTACT_ROWS.map(({ Icon, text }) => (
                                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <Icon size={12} color={BADGE_COLORS.teal} strokeWidth={2.5} />
                                        <span>{text}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ flex: 1 }} />
                            <div style={{ marginBottom: 21 }} />
                        </div>
                    </div>
                </motion.div>
            </div>

            <button onClick={onFlip} style={{
                display: 'flex', alignItems: 'center', gap: 6, margin: '12px auto 0', padding: '7px 16px',
                borderRadius: 99, border: `1px solid ${BADGE_COLORS.teal}`, background: BADGE_COLORS.tealChip,
                color: BADGE_COLORS.tealDark, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>
                <RotateCw size={13} /> {flipped ? 'Show Front' : 'Show Back'}
            </button>
        </div>
    );
};

export default BadgeCardPreview;
